import { PartyGroup, PartyGroupDto, PartyGroupListItem, PartyGroupMin, PartyGroupUpdateDto } from 'types/groups';
import { HistoryDto, HistoryEntityDto, HistoryEntityType, HistoryFieldType, UpdateHistoryDto } from 'types/histories';
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APIGroupsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'PartyGroupListItems',
            'HistoryListItems',
            'PartyGroupsMin',
            'PartyGroup',
            'PartyGroupEmployeeHistory',
            'PartyGroupPartyHistory',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPartyGroupById: builder.query<PartyGroup, string>({
                query: (entityId) => `/v1/party-groups/${entityId}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => (arg ? [{ type: 'PartyGroup', id: arg }] : []),
            }),
            getPartyGroupEmployeeHistory: builder.query<HistoryDto[], { id: string; type: string }>({
                query: ({ id, type }) => `/v1/party-groups/${id}/employees/${type}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => (arg.id ? [{ type: 'PartyGroupEmployeeHistory', id: arg.id }] : []),
            }),
            getPartyGroupPartyHistory: builder.query<HistoryDto[], { id: string; type: string }>({
                query: ({ id, type }) => `/v1/party-groups/${id}/parties/${type}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => (arg.id ? [{ type: 'PartyGroupPartyHistory', id: arg.id }] : []),
            }),
            getMinGroupsListWithFilters: accumulatedQueryWithFilters<PartyGroupMin>(builder, '/v1/party-groups/min', {
                providesTags: ['PartyGroupsMin'],
            }),
            getGroupsWithFilters: accumulatedQueryWithFilters<PartyGroupListItem>(builder, '/v1/party-groups', {
                providesTags: ['PartyGroupListItems'],
            }),
            addPartyGroup: builder.mutation<PartyGroup, PartyGroupDto>({
                query: ({ ...body }) => ({
                    url: `/v1/party-groups`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['PartyGroupListItems'],
            }),
            updatePartyGroup: builder.mutation<PartyGroup, PartyGroupUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/party-groups/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'PartyGroupListItems',
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.PARTY_GROUP,
                                      entityId: arg.id,
                                      fieldType: HistoryFieldType.RELATIONSHIP_MANAGER_EMPLOYEE,
                                  },
                              },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: HistoryEntityType.PARTY_GROUP,
                                      entityId: arg.id,
                                      fieldType: HistoryFieldType.RELATIONSHIP_PARTNER_EMPLOYEE,
                                  },
                              },
                          ]
                        : ['PartyGroupListItems', 'HistoryListItems'],
            }),
            updatePartyGroupEmployeeHistoryEntry: builder.mutation<HistoryDto, HistoryEntityDto & UpdateHistoryDto>({
                query: ({ id, entityId, ...body }) => ({
                    url: `/v1/party-groups/${entityId}/employees/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.entityId
                        ? [
                              { type: 'PartyGroupEmployeeHistory', id: arg.entityId },
                              { type: 'PartyGroup', id: String(arg.entityId) },
                          ]
                        : [],
            }),
            updatePartyGroupPartyHistoryEntry: builder.mutation<HistoryDto, HistoryEntityDto & UpdateHistoryDto>({
                query: ({ id, entityId, ...body }) => ({
                    url: `/v1/party-groups/${entityId}/parties/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.entityId
                        ? [
                              { type: 'PartyGroupPartyHistory', id: arg.entityId },
                              { type: 'PartyGroup', id: String(arg.entityId) },
                          ]
                        : [],
            }),
            deletePartyGroupEmployeeHistoryEntry: builder.mutation<undefined, HistoryEntityDto>({
                query: ({ entityId, id }) => ({
                    url: `/v1/party-groups/${entityId}/employees/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'PartyGroupEmployeeHistory', id: arg.entityId },
                    { type: 'PartyGroup', id: String(arg.entityId) },
                ],
            }),
            deletePartyGrouppartyHistoryEntry: builder.mutation<undefined, HistoryEntityDto>({
                query: ({ entityId, id }) => ({
                    url: `/v1/party-groups/${entityId}/parties/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'PartyGroupPartyHistory', id: arg.entityId },
                    { type: 'PartyGroup', id: String(arg.entityId) },
                ],
            }),
            deletePartyGroup: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/party-groups/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['PartyGroupListItems'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetPartyGroupByIdQuery,
    useLazyGetPartyGroupByIdQuery,
    useGetPartyGroupEmployeeHistoryQuery,
    useUpdatePartyGroupEmployeeHistoryEntryMutation,
    useDeletePartyGroupEmployeeHistoryEntryMutation,
    useGetPartyGroupPartyHistoryQuery,
    useUpdatePartyGroupPartyHistoryEntryMutation,
    useDeletePartyGrouppartyHistoryEntryMutation,
    useLazyGetMinGroupsListWithFiltersQuery,
    useLazyGetGroupsWithFiltersQuery,
    useAddPartyGroupMutation,
    useUpdatePartyGroupMutation,
    useDeletePartyGroupMutation,
} = APIGroupsSlice;
